import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Camera, MapPin, Calendar as CalIcon } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Pill } from "./common";

export default function EmployeeJobs() {
  const [jobs, setJobs] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/jobs"); setJobs(data); } catch { setJobs([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const complete = async (jobId, file) => {
    setBusy(jobId);
    try {
      const fd = new FormData();
      if (file) fd.append("photo", file);
      await api.post(`/jobs/${jobId}/complete`, fd);
      toast.success("Job marked complete — customer notified");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="employee-jobs">
      <h2 className="font-display text-2xl font-600 text-charcoal mb-1">My Jobs</h2>
      <p className="text-olive mb-6">Your assigned work. Mark each job complete and add a photo when you're done.</p>
      {jobs === null ? <Loader2 className="w-5 h-5 animate-spin text-olive" /> : jobs.length === 0 ? (
        <p className="text-olive" data-testid="employee-jobs-empty">No jobs assigned to you yet.</p>
      ) : (
        <div className="space-y-3">
          {jobs.map((j) => (
            <div key={j.id} className="bg-white border border-border rounded-sm p-5" data-testid={`emp-job-${j.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2"><span className="font-600 text-charcoal">{j.service_type}</span><Pill status={j.status} /></div>
                  <p className="text-olive text-sm mt-1 flex items-center gap-1.5"><CalIcon className="w-4 h-4" /> {(j.service_date || "").slice(0, 16).replace("T", " at ")}</p>
                  {j.notes && <p className="text-olive text-sm mt-1 flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {j.notes}</p>}
                </div>
                {j.status !== "completed" && (
                  <div className="flex gap-2">
                    <label className="inline-flex items-center gap-1.5 bg-charcoal/5 text-charcoal px-3 py-2 rounded-sm text-sm font-600 cursor-pointer hover:bg-charcoal/10" data-testid={`emp-photo-label-${j.id}`}>
                      <Camera className="w-4 h-4" /> Photo + Complete
                      <input type="file" accept="image/*" className="hidden" data-testid={`emp-photo-${j.id}`} onChange={(e) => e.target.files?.[0] && complete(j.id, e.target.files[0])} />
                    </label>
                    <button onClick={() => complete(j.id, null)} disabled={busy === j.id} data-testid={`emp-complete-${j.id}`} className="inline-flex items-center gap-1.5 bg-emerald text-white px-4 py-2 rounded-sm text-sm font-600 hover:bg-emerald-dark disabled:opacity-60">
                      {busy === j.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Complete
                    </button>
                  </div>
                )}
                {j.status === "completed" && <span className="text-emerald text-sm font-600 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Done</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
